import type { LibraryItemView } from '@/lib/api/generated/model';

/**
 * The one action an item's state allows, or none (spec 104 FR-008).
 *
 * Decided here, once, so the card and the drawer can never disagree about
 * what to offer. A workflow that the library has moved on from offers nothing:
 * it is updated by deleting and reinstalling, which the drawer explains.
 */
export function actionFor(item: LibraryItemView): 'install' | 'update' | null {
  if (!item.installable) return null;
  if (item.state === 'not_installed') return 'install';
  if (item.state === 'update_available' && item.kind !== 'workflow')
    return 'update';
  return null;
}

/**
 * Whether this item can be taken back out from here (spec 106 FR-003 to FR-006).
 *
 * A workflow cannot: it is a row with tasks and history hanging off it rather
 * than a folder, and the page already says to delete one on its own page. A
 * copy changed here cannot either — the service refuses it, and offering a
 * button that will be refused is worse than offering none.
 */
export function canRemove(item: LibraryItemView): boolean {
  if (!item.installable || item.kind === 'workflow') return false;
  return item.state === 'installed' || item.state === 'update_available';
}

/**
 * Why an item that is present cannot be removed from here, or nothing.
 *
 * Only for the cases a reader would otherwise have to guess at: a copy they
 * changed, and a folder the library never put there. Silence everywhere else.
 */
export function removalBlockedBecause(item: LibraryItemView): string | null {
  if (item.kind === 'workflow' || !item.installable) return null;
  if (
    item.state === 'customized' ||
    item.state === 'update_available_with_local_changes'
  ) {
    return 'This copy was changed here, so it is not removed from the Library. Delete it on the files page, where you can read it first.';
  }
  if (item.state === 'added_by_hand') {
    return 'The library did not put this here. Delete it on the files page.';
  }
  return null;
}
